import { useEffect, useState } from "react";
import "./App.css";
import "./assets/css/animate.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import api from "./api";
import { useDispatch, useSelector } from "react-redux";

import { Home } from "./pages/landingPage";
import { NavbarAdmin } from "./pages/admin/navbarAdmin";
import Sidebar from "./pages/admin/sidebar";
import { ToastContainer } from "react-toastify";
import { Dashboard } from "./pages/admin/dashboard";
import { AdminLogin } from "./pages/admin/login";
import { SupportLogin } from "./pages/support/login";
import { Vehicle } from "./pages/admin/vehicle";
import { notify } from "./utils/toast";

import adminAction from "./redux/admin/action";
import supportAction from "./redux/support/action";
import { Driver } from "./pages/admin/driver";
import { AdminRoute } from "./pages/admin/route";
import { Students } from "./pages/admin/students";
import { AdminFuel } from "./pages/admin/fuel";
import { PaymentMethod } from "./pages/admin/paymentType";
import { Maintenance } from "./pages/admin/maintenance";
import { Salary } from "./pages/admin/salary";
import { Loans } from "./pages/admin/loan";
import { Fees } from "./pages/admin/fee";
import { Enquiries } from "./pages/admin/contact";
import { AdminBookings } from "./pages/admin/bookings";
import { BookingPage } from "./pages/landingPage/booking";
import { FeeReceipts } from "./pages/admin/feeReceipts";
import { Entries } from "./pages/admin/entries";
import { Bill } from "./pages/admin/bill";
import { SupportMembers } from "./pages/admin/supportMembers";

function App() {
  const adminStore   = useSelector((state) => state.adminReducer);
  const supportStore = useSelector((state) => state.supportReducer);
  const [authType, setAuthType] = useState("");
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const { setAdmin } = adminAction;
  const { setSupportMember } = supportAction;

  /* ── helpers ── */
  const isAdminLoggedIn   = !!adminStore?.admin?.admin?.email;
  const isSupportLoggedIn = !!supportStore?.supportMember?.member?.email;
  const isLoggedIn        = isAdminLoggedIn || isSupportLoggedIn;
  // null means admin (all pages); array means support (only listed pages)
  const supportPermissions = isSupportLoggedIn
    ? (supportStore?.supportMember?.member?.permissions || [])
    : null;

  const can = (key) => supportPermissions === null || supportPermissions.includes(key);

  /* ── Admin session check ── */
  const checkAdminSession = async () => {
    try {
      setLoader(true);
      const token = localStorage.getItem("admintoken");
      if (token) {
        const res = await api.verifyAdminSession({ token });
        if (res?.token) {
          axios.defaults.headers.common["Authorization"] = res.token;
          setAuthType("admin");
        } else {
          dispatch(setAdmin({}));
          localStorage.removeItem("admintoken");
          window.location.href = "/";
        }
      }
    } catch {
      notify.error("Session Expired");
      dispatch(setAdmin({}));
      localStorage.removeItem("admintoken");
    } finally {
      setLoader(false);
    }
  };

  /* ── Support session check ── */
  const checkSupportSession = async () => {
    try {
      setLoader(true);
      const token = localStorage.getItem("supporttoken");
      if (token) {
        const res = await api.verifySupportSession({ token });
        if (res?.isLoggedIn && res?.member) {
          axios.defaults.headers.common["Authorization"] = token;
          setAuthType("support");
          dispatch(setSupportMember(res));
        } else {
          dispatch(setSupportMember({}));
          localStorage.removeItem("supporttoken");
          window.location.href = "/support-login";
        }
      }
    } catch {
      dispatch(setSupportMember({}));
      localStorage.removeItem("supporttoken");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (isAdminLoggedIn)   checkAdminSession();
    else if (isSupportLoggedIn) checkSupportSession();
  }, []);

  // Horizontal scroll with mouse wheel on .table-responsive
  useEffect(() => {
    const handler = (e) => {
      const el = e.target.closest(".table-responsive");
      if (!el) return;
      if (el.scrollWidth <= el.clientWidth) return;
      if (e.shiftKey) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    window.addEventListener("wheel", handler, { passive: false });
    return () => window.removeEventListener("wheel", handler);
  }, []);

  return (
    <div className="App">
      {isLoggedIn ? (
        <div id="wrapper">
          <Sidebar authType={authType} setAuthType={setAuthType} supportPermissions={supportPermissions} />
          <div id="content-wrapper" className="d-flex flex-column">
            <div id="content">
              <NavbarAdmin setAuthType={setAuthType} authType={authType} />
              <Routes>
                {can("dashboard")     && <Route path="/admin/dashboard"       element={<Dashboard />} />}
                {can("vehicles")      && <Route path="/admin/vehicles"         element={<Vehicle />} />}
                {can("drivers")       && <Route path="/admin/drivers"          element={<Driver />} />}
                {can("routes")        && <Route path="/admin/routes"           element={<AdminRoute />} />}
                {can("students")      && <Route path="/admin/students"         element={<Students />} />}
                {can("fuel")          && <Route path="/admin/fuel"             element={<AdminFuel />} />}
                {can("maintenance")   && <Route path="/admin/maintenance"      element={<Maintenance />} />}
                {can("salary")        && <Route path="/admin/salary"           element={<Salary />} />}
                {can("loans")         && <Route path="/admin/loans"            element={<Loans />} />}
                {can("paymentMethod") && <Route path="/admin/payment-method"   element={<PaymentMethod />} />}
                {can("fees")          && <Route path="/admin/fees"             element={<Fees />} />}
                {can("enquiries")     && <Route path="/admin/enquiries"        element={<Enquiries />} />}
                {can("bookings")      && <Route path="/admin/bookings"         element={<AdminBookings />} />}
                {can("feeReceipts")   && <Route path="/admin/fee-receipts"     element={<FeeReceipts />} />}
                {can("entry")         && <Route path="/admin/entry"            element={<Entries />} />}
                {can("bill")          && <Route path="/admin/bill"             element={<Bill />} />}
                {/* Support Members — admin only */}
                {isAdminLoggedIn      && <Route path="/admin/support-members"  element={<SupportMembers />} />}
              </Routes>
            </div>
          </div>
        </div>
      ) : (
        <Routes>
          <Route path="/"               element={<Home />} />
          <Route path="/admin-login"    element={<AdminLogin setAuthType={setAuthType} />} />
          <Route path="/support-login"  element={<SupportLogin setAuthType={setAuthType} />} />
          <Route path="/booking"        element={<BookingPage />} />
        </Routes>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

export default App;
